import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
    Row, Col, Typography, Button, Input, Flex, Card, Spin, Tag, Table, Collapse, Tooltip,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';

import { sendChatMessage, setChatMessages } from './redux/slice';
import { primary, secondary, muted } from '../../utils/theme/config';

const { Title, Text } = Typography;

const SUGGESTED_QUESTIONS = [
    'Wie viel bezahle ich monatlich?',
    'Welche Verträge laufen bald aus?',
    'Welche Verträge verlängern sich automatisch?',
    'Wo kann ich Kosten sparen?',
];

const statusLabels = {
    active: 'Aktiv',
    cancelled: 'Gekündigt',
    expired: 'Abgelaufen',
    archived: 'Archiviert',
};

const statusColors = {
    active: primary,
    cancelled: secondary,
    expired: '#fa8c16',
    archived: muted,
};

const TypingIndicator = () => (
    <Flex gap={4} align='center' style={{ padding: '8px 0' }}>
        {[0, 1, 2].map((i) => (
            <div
                key={i}
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: primary,
                    animation: `bounce 1.2s infinite ${i * 0.2}s`,
                }}
            />
        ))}
        <style>{`
            @keyframes bounce {
                0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                30% { transform: translateY(-6px); opacity: 1; }
            }
        `}</style>
    </Flex>
);

const ContractSupportTable = ({ contracts }) => {
    const navigate = useNavigate();
    if (!contracts || contracts.length === 0) return null;
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            render: (name, rec) => rec._id
                ? <Link to={`/contract-manager/detail/${rec._id}`} onClick={(e) => e.stopPropagation()}>{name}</Link>
                : name,
        },
        { title: 'Partner', dataIndex: 'partner', key: 'partner', ellipsis: true },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s) => <Tag color={statusColors[s] || muted}>{statusLabels[s] || s}</Tag>,
        },
        {
            title: 'Kosten',
            dataIndex: 'costAmount',
            key: 'costAmount',
            render: (val, rec) => val
                ? `${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)} / ${rec.costInterval || ''}`
                : '—',
        },
        {
            title: '',
            key: 'action',
            width: 60,
            render: (_, rec) => rec._id
                ? (
                    <Tooltip title='Vertrag öffnen'>
                        <Button
                            type='link'
                            size='small'
                            style={{ padding: 0 }}
                            onClick={() => navigate(`/contract-manager/detail/${rec._id}`)}
                        >
                            Öffnen
                        </Button>
                    </Tooltip>
                )
                : null,
        },
    ];
    return (
        <Collapse
            size='small'
            items={[{
                key: '1',
                label: <Text style={{ fontSize: 12 }}>Relevante Verträge ({contracts.length})</Text>,
                children: (
                    <Table
                        columns={columns}
                        dataSource={contracts}
                        rowKey={(r) => r._id || r.name}
                        pagination={false}
                        size='small'
                        onRow={(rec) => ({
                            onClick: () => rec._id && navigate(`/contract-manager/detail/${rec._id}`),
                            style: { cursor: rec._id ? 'pointer' : 'default' },
                        })}
                    />
                ),
            }]}
            style={{ marginTop: 8 }}
        />
    );
};

const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    return (
        <Flex justify={isUser ? 'flex-end' : 'flex-start'} style={{ marginBottom: 12 }}>
            <div
                style={{
                    maxWidth: '75%',
                    background: isUser ? primary : '#f0f0f0',
                    color: isUser ? '#fff' : '#000',
                    borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    padding: '10px 14px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                }}
            >
                <Text style={{ color: isUser ? '#fff' : '#000', whiteSpace: 'pre-wrap' }}>
                    {message.content}
                </Text>
                {!isUser && message.contracts && message.contracts.length > 0 && (
                    <ContractSupportTable contracts={message.contracts} />
                )}
                <div style={{ textAlign: isUser ? 'right' : 'left', marginTop: 4 }}>
                    <Text style={{ fontSize: 10, color: isUser ? 'rgba(255,255,255,0.7)' : muted }}>
                        {message.timestamp ? new Date(message.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </Text>
                </div>
            </div>
        </Flex>
    );
};

const ContractChat = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { chatMessages, chatLoading } = contracts;
    const organizer = auth?.decodeToken?.organizer;

    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, chatLoading]);

    const handleSend = (text) => {
        const msg = (text || inputText).trim();
        if (!msg || !organizer) return;
        setInputText('');
        dispatch(sendChatMessage({ message: msg, organizer }));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        dispatch(setChatMessages([]));
    };

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>Vertrags-KI-Chat</Title>
                </Col>
                <Col>
                    <Button onClick={handleClear} disabled={chatMessages.length === 0}>
                        Chat leeren
                    </Button>
                </Col>
            </Row>

            <Card
                className='border-green'
                style={{ marginTop: 12, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 240px)', minHeight: 500 }}
                bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0 }}
            >
                {/* Messages */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px 20px',
                        background: '#fafafa',
                    }}
                >
                    {chatMessages.length === 0 && !chatLoading ? (
                        <Flex justify='center' align='center' vertical gap={8} style={{ height: '100%', minHeight: 200 }}>
                            <Text type='secondary' style={{ fontSize: 16 }}>Wie kann ich Ihnen heute helfen?</Text>
                            <Text type='secondary' style={{ fontSize: 13 }}>Stellen Sie eine Frage zu Ihren Verträgen</Text>
                        </Flex>
                    ) : (
                        <Fragment>
                            {chatMessages.map((msg, idx) => (
                                <MessageBubble key={idx} message={msg} />
                            ))}
                            {chatLoading && (
                                <Flex justify='flex-start'>
                                    <div style={{
                                        background: '#f0f0f0',
                                        borderRadius: '16px 16px 16px 4px',
                                        padding: '8px 14px',
                                    }}>
                                        <TypingIndicator />
                                    </div>
                                </Flex>
                            )}
                            <div ref={messagesEndRef} />
                        </Fragment>
                    )}
                </div>

                {/* Suggested Questions */}
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 16px', background: '#fff' }}>
                    <Flex wrap gap={6}>
                        {SUGGESTED_QUESTIONS.map((q) => (
                            <Tag
                                key={q}
                                style={{ cursor: 'pointer', borderColor: primary, color: primary, borderRadius: 12 }}
                                onClick={() => handleSend(q)}
                            >
                                {q}
                            </Tag>
                        ))}
                    </Flex>
                </div>

                {/* Input */}
                <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #f0f0f0' }}>
                    <Flex gap={8}>
                        <Input.TextArea
                            value={inputText}
                            placeholder='Fragen Sie zu Ihren Verträgen...'
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            style={{ flex: 1 }}
                            disabled={chatLoading}
                        />
                        <Button
                            type='primary'
                            onClick={() => handleSend()}
                            loading={chatLoading}
                            disabled={!inputText.trim()}
                            style={{ alignSelf: 'flex-end' }}
                        >
                            Senden
                        </Button>
                    </Flex>
                </div>
            </Card>
        </Fragment>
    );
};

export default ContractChat;
